import { type FC, type ReactNode, useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import classNames from 'classnames';
import { ChainflipLogo } from '@/shared/assets/chainflip-logo';
import { ChainflipNetworkBadge } from '@/shared/components/atoms/ChainflipNetworkBadge';
import { Link, type LinkProps } from '@/shared/components/atoms/Link';
import NavbarDropdown, { type DropdownProps } from '@/shared/components/flip-ui-kit/NavbarDropdown';
import useScreenSize from '@/shared/hooks/useScreenSize';
import CloseIcon from '@/shared/icons/flip-ui-kit/large/Close';
import HamburgerIcon from '@/shared/icons/flip-ui-kit/large/Hamburger';

interface ExtendedLinkProps extends LinkProps {
  type: 'link';
  activePath?: string;
  display?: 'desktop' | 'mobile';
  Icon?: FC;
  newBadge?: boolean;
}

interface ExtendedDropdownProps extends DropdownProps {
  type: 'dropdown';
  display?: 'desktop' | 'mobile';
  title: string;
}

export type NavItemProps = ExtendedLinkProps | ExtendedDropdownProps;

const LinkWithTransition = ({
  currentPathName,
  href,
  activePath,
  children,
  Icon,
  newBadge,
  ...restProps
}: {
  currentPathName: string;
} & ExtendedLinkProps) => {
  const hrefBase = /^(\/.+?)(\/|$)/.exec(href)?.[1];
  const isActive = Boolean(hrefBase && currentPathName.startsWith(hrefBase));

  return (
    <div className="flex items-center gap-x-2">
      <Link
        {...restProps}
        href={href}
        color="white"
        underline={isActive}
        className={classNames(
          'font-aeonikMedium text-14 transition-colors duration-150 ease-out',
          isActive && '!text-cf-green-1',
          Icon && 'flex items-center gap-x-1',
        )}
      >
        {Icon && <Icon />}
        {children}
      </Link>
      {newBadge && (
        <span className="rounded-[3px] border border-[rgba(70,218,147,.20)] bg-cf-green-4 px-1 font-aeonikMedium text-[9px] leading-[18px] text-cf-green-3">
          New
        </span>
      )}
    </div>
  );
};

const Navbar = ({
  logoHref,
  isFixed = false,
  className,
  navItems,
  actions,
  hamburgerReplacement,
  hideNetworkBadgeOnMdDevices = false,
  middleSection,
  noContainer = false,
}: {
  logoHref: string;
  isFixed?: boolean;
  className?: string;
  navItems?: NavItemProps[];
  actions?: ReactNode;
  hamburgerReplacement?: ReactNode;
  hideNetworkBadgeOnMdDevices?: boolean;
  middleSection?: ReactNode;
  noContainer?: boolean;
}): React.JSX.Element => {
  const [openNavModal, setOpenNavModal] = useState(false);
  const { pathname } = useRouter();
  const { isMobile } = useScreenSize();

  useEffect(() => {
    if (openNavModal) {
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [openNavModal]);

  return (
    <div
      className={classNames(
        'flex h-[74px] w-full flex-col items-center justify-center py-4',
        isFixed ? 'fixed z-[1000] bg-[rgba(13,13,13,0.70)] backdrop-blur-[20px]' : 'z-10',
        className,
      )}
      style={{ marginTop: 'var(--banner-height)' }}
    >
      {!openNavModal && (
        <div
          className={classNames(
            'flex w-full items-center justify-between gap-x-3 px-4 md:px-8',
            !noContainer && '2xl:container',
          )}
        >
          <div className="flex items-center gap-x-[25px]">
            <Link href={logoHref}>
              <ChainflipLogo />
            </Link>
            <div className={classNames(hideNetworkBadgeOnMdDevices && 'hidden lg:block')}>
              <ChainflipNetworkBadge disabled={isMobile} />
            </div>
          </div>
          <div className="hidden grow items-center justify-end text-14 md:flex md:gap-x-6 lg:gap-x-12">
            {middleSection && pathname !== '/' && (
              <div className="mr-auto w-full max-w-[400px] transition-[max-width] duration-200 ease-[cubic-bezier(0.23,1,0.32,1)] focus-within:max-w-[512px]">
                {middleSection}
              </div>
            )}
            {navItems
              ?.filter((item) => item.display !== 'mobile')
              .map((item) => {
                if (item.type === 'link') {
                  return (
                    <LinkWithTransition key={item.href} currentPathName={pathname} {...item} />
                  );
                }
                if (item.type === 'dropdown') {
                  return <NavbarDropdown key={item.title} {...item} />;
                }

                return null;
              })}
            <div className="flex items-center gap-x-2">{actions}</div>
          </div>
          <div className="md:hidden">
            {hamburgerReplacement ?? (
              <button type="button" onClick={() => setOpenNavModal(true)}>
                <HamburgerIcon />
              </button>
            )}
          </div>
        </div>
      )}
      {openNavModal && (
        <>
          <div className="flex w-full items-center justify-between px-4">
            <div className="flex items-center gap-x-[25px]">
              <Link href={logoHref}>
                <ChainflipLogo />
              </Link>
            </div>
            <button type="button" className="ml-auto" onClick={() => setOpenNavModal(false)}>
              <CloseIcon className="cursor-pointer" />
            </button>
          </div>
          <div className="fixed top-[74px] z-20 flex h-[calc(100vh-74px)] w-full flex-col overflow-y-scroll bg-black px-4">
            <div className="flex flex-col [&>*]:border-b [&>*]:border-b-cf-gray-3 [&>*]:py-5">
              {middleSection}
              {navItems
                ?.filter((item) => item.display !== 'desktop')
                .map((item) => {
                  if (item.type === 'link') {
                    return (
                      <LinkWithTransition key={item.href} currentPathName={pathname} {...item} />
                    );
                  }
                  if (item.type === 'dropdown') {
                    return <NavbarDropdown key={item.title} {...item} />;
                  }

                  return null;
                })}
              {actions}
              <ChainflipNetworkBadge titleSizeClass="text-16" />
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Navbar;
